import { WorkExportInput } from './../../../../types/src/services/input-type/work/work.export-input';
import type {
    PrepareWorkExportInput,
} from '@libs/types/src/services/input-type/work/start-work-export.input';
import type {
    WorkExportPreparationObject,
} from '@libs/types/src/services/object-type/work/work-export-preparation.object';
import { TangibleDepositExportInput } from './../../../../types/src/services/input-type/work/tangible-deposit-export.input';
import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    CreateWorkInput,
    DeletePerformedProcedureInput,
    DeleteWorkInput,
    GetServiceInput,
    MedicalProcedureGridObject,
    OkObject,
    PatchServiceInput,
    PerformedProcedureCreateInput,
    PerformedProcedureGridObject,
    ServiceBasicInfoCreateInput,
    ServiceBasicInfoDeleteInput,
    ServiceBasicInfoGridObject,
    ServiceBasicInfoUpdateInput,
    ServiceCreateInput,
    ServiceGridObject,
    ServiceObject,
    ServicePaymentCreateInput,
    ServicePaymentDeleteInput,
    ServicePaymentGetInput,
    ServicePaymentGridObject,
    ServicePaymentMedicalProcedureCreateInput,
    ServicePaymentMedicalProcedureDeleteInput,
    ServicePaymentMedicalProcedureGridObject,
    ServicePaymentMedicalProcedureUpdateInput,
    ServicePaymentObject,
    ServicePaymentUpdateInput,
    ServiceTypeGridObject,
    UpdatePerformedProcedureInput,
    UpdateWorkInput,
    VoucherPrescribedMedicalProcedureGridObject,
    WorkGridObject,
    WorkObject,
} from '@it2go/types';
import { filterFrag } from './types/filter.types';
import {
    doctorSpecializationFrag,
    medicalProcedureFrag,
    serviceBasicInfoGridItemFrag,
    serviceDataShareFrag,
    serviceFrag,
    serviceGridDetailFrag,
    serviceGridItemFrag,
    serviceMedicalProcedureGridItemFrag,
    serviceNursingCareDiagnosisCodeGridItemFrag,
    serviceNursingCareDiagnosisGroupGridItemFrag,
    servicePatternObjectFrag,
    servicePaymentFrag,
    servicePaymentGridItemFrag,
    servicePaymentMedicalProcedureGridItemFrag,
    serviceSettingsFrag,
    serviceTypeGridItemFrag,
} from './types/service.types';
import {
    performedProcedureGridItemFrag,
    performedProcedureGridItemSearchFrag,
    workFrag,
    workGridItemFrag,
} from './types/work.types';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import { IdInput, IdsInput } from '@libs/types/src/common/input-type/id.input';
import {
    ServiceMedicalProcedureUpdateInput,
} from '@libs/types/src/services/input-type/services/service-medical-procedure.update-input';
import {
    ServiceMedicalProcedureCreateInput,
} from '@libs/types/src/services/input-type/services/service-medical-procedure.create-input';
import { StartWorkElectronicSignatureInput, CheckWorkElectronicSignatureInput } from '@libs/types/src/services/input-type/work/work-signature.input';
import { WorkElectronicSignatureObject, WorkElectronicSignatureStatusObject } from '@libs/types/src/services/object-type/work/work-signature.object';
import {
    ServicePatternDocumentCreateInput,
    ServicePatternStructureCreateInput,
} from '@libs/types/src/services/input-type/services/service-pattern.create-input';
import {
    ServicePatternGridObject,
    ServicePatternObject,
} from '@libs/types/src/services/object-type/service/service-pattern.object';
import { ServicePatternDeleteInput } from '@libs/types/src/services/input-type/services/service-pattern-delete.input';
import {
    ServicePatternDocumentUpdateInput,
    ServicePatternStructureUpdateInput,
} from '@libs/types/src/services/input-type/services/service-pattern.update-input';
import { ProcessPatternInput } from '@libs/types/src/services/input-type/services/process-pattern.input';
import {
    DoctorSpecializationGridObject,
    DoctorSpecializationObject,
} from '@libs/types/src/services/object-type/vzp/doctor-specialization.object';
import {
    DoctorSpecializationCreateInput,
} from '@libs/types/src/services/input-type/vzp/doctor-specialization.create-input';
import {
    DoctorSpecializationUpdateInput,
} from '@libs/types/src/services/input-type/vzp/doctor-specialization.update-input';
import type {
    DoctorSpecializationImportInput,
} from '@libs/types/src/services/input-type/vzp/doctor-specialization.import-input';
import { voucherPrescribedMedicalProcedureFrag } from './types/voucher.types';
import {
    ServiceNursingCareDiagnosisCodeGridObject,
} from '@libs/types/src/services/object-type/service/service-nursing-care-diagnosis-code.object';
import {
    ServiceNursingCareDiagnosisCodeCreateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-code.create-input';
import {
    ServiceNursingCareDiagnosisCodeUpdateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-code.update-input';
import {
    ServiceNursingCareDiagnosisGroupGridObject,
} from '@libs/types/src/services/object-type/service/service-nursing-care-diagnosis-group.object';
import {
    ServiceNursingCareDiagnosisGroupCreateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-groups.create-input';
import {
    ServiceNursingCareDiagnosisGroupUpdateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-groups.update-input';
import { GqlQueryOptions, GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { ServiceDataShareGridObject } from '@libs/types/src/services/object-type/service/service-data-share.object';
import {
    ServiceDataShareCreateInput,
} from '@libs/types/src/services/input-type/services/service-data-share.create-input';
import {
    ServiceDataShareUpdateInput,
} from '@libs/types/src/services/input-type/services/service-data-share.update-input';
import { historyGridItemFrag } from './types/history-item.types';
import { AuditLogGridObject } from '@libs/types/src/object-history/object-type/audit-log.object';
import {
    ServiceMedicalProcedureGridObject,
} from '@libs/types/src/services/object-type/service/service-medical-procedure.object';
import {
    MedicalProcedureGridHistoryObject,
} from '@libs/types/src/services/object-type/medical-procedure-history.object';
import { WorkGetClosestInput } from '@libs/types/src/services/input-type/work/work.get-closest-input';
import { WorkClosestObject } from '@libs/types/src/services/object-type/work/work.closest-object';
import { WorkUpsertInput } from '@libs/types/src/services/input-type/work/work.upsert-input';
import {
    ProcessPatternContentObject,
} from '@libs/types/src/services/object-type/service/process-pattern-content.object';
import { serviceLocationItemFrag } from './types/service-settings.types';
import { ServiceLocationGridObject } from '@libs/types/src/services/object-type/service/service-location.object';

@Injectable({ providedIn: 'root' })
export class ServiceGqlServiceV2 extends GqlServiceV2 {

    public getServices(input: GridInput | null = null, frag: string = serviceGridItemFrag, options: GqlQueryOptions = {}) {
        return this.query<ServiceGridObject>(
            `
                query($input: GridInput) {
                    service {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public getAllServices(input: GridInput | null = null) {
        return this.query<ServiceGridObject>(
            `
                query($input: GridInput) {
                    service {
                        all(input: $input) {
                            ${filterFrag}
                            items {
                                id
                                name
                                expertise {
                                    code
                                }
                                type
                                serviceTypeId
                                serviceSettings {
                                    ${serviceSettingsFrag}
                                }
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceDetail(input: GridInput | null, frag: string = serviceGridDetailFrag) {
        return this.query<ServiceGridObject>(
            `
                query($input: GridInput) {
                    service {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    /**
     * @Deprecated()
     */
    public getService(input: GetServiceInput, frag: string = serviceFrag) {
        return this.query<ServiceObject>(`
                query ($input: GetServiceInput!) {
                    service {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createService(input: ServiceCreateInput, frag: string = serviceFrag) {
        return this.mutate<ServiceObject>(
            `
                mutation($input: ServiceCreateInput!) {
                    service {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteService(input: IdInput, fragment: string = 'ok') {
        return this.mutate<ServiceObject>(
            `
                mutation($input: IdInput!) {
                    service {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public patchService(input: PatchServiceInput, frag: string = serviceFrag) {
        return this.mutate<ServiceObject>(
            `
                mutation($input: PatchServiceInput!) {
                    service {
                        patch(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceTypes(input: GridInput | null = null, frag: string = serviceTypeGridItemFrag) {
        return this.query<ServiceTypeGridObject>(
            `
                query($input: GridInput) {
                    serviceType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getMedicalProcedures(input: GridInput | null, frag: string = medicalProcedureFrag) {
        return this.query<MedicalProcedureGridObject>(
            `
                query($input: GridInput) {
                    medicalProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getWorks(input: GridInput | null, frag: string = workGridItemFrag) {
        return this.query<WorkGridObject>(
            `
                query($input: GridInput) {
                    work {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteWork(input: DeleteWorkInput) {
        return this.mutate(
            `
                mutation($input: DeleteWorkInput!) {
                    work {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public batchDeleteWork(input: IdsInput, fragment = 'ok') {
        return this.mutate(
            `
                mutation($input: IdsInput!) {
                    work {
                        batchDelete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createWork(input: CreateWorkInput, frag: string = workFrag) {
        return this.mutate<WorkObject>(
            `
                mutation($input: CreateWorkInput!) {
                    work {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
            { delay: true },
        );
    }

    public updateWork(input: UpdateWorkInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdateWorkInput!) {
                    work {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public upsertWork(input: WorkUpsertInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: WorkUpsertInput!) {
                    work {
                        upsert(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public startSoftware602WorkSignature(input: StartWorkElectronicSignatureInput) {
        return this.mutate<WorkElectronicSignatureObject>(
            `
                mutation($input: StartWorkElectronicSignatureInput!) {
                    work {
                        startSoftware602Signature(input: $input) {
                            ok
                            signingUrl
                            jobOperationUrl
                            jobOperationIdent
                            providerFileIds
                            message
                        }
                    }
                }
            `,
            { input },
        );
    }

    public checkSoftware602WorkSignature(input: CheckWorkElectronicSignatureInput) {
        return this.mutate<WorkElectronicSignatureStatusObject>(
            `
                mutation($input: CheckWorkElectronicSignatureInput!) {
                    work {
                        checkSoftware602Signature(input: $input) {
                            done
                            status
                            signedFileIds
                            message
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getClosestWorks(input: WorkGetClosestInput, fragment: string) {
        return this.query<WorkClosestObject>(
            `
                query($input: WorkGetClosestInput!) {
                    workAddress {
                        getClosest(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getPerformedProcedures(input: GridInput | null, frag: string = performedProcedureGridItemFrag) {
        return this.query<PerformedProcedureGridObject>(
            `
                query($input: GridInput) {
                    performedProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    /**
     * Used in globalSearch
     * @param input
     */
    public getSearchPerformedProcedure(input: GridInput | null, frag: string = performedProcedureGridItemSearchFrag) {
        return this.query<PerformedProcedureGridObject>(
            `
                query($input: GridInput) {
                    performedProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createPerformedProcedure(input: PerformedProcedureCreateInput) {
        return this.mutate(
            `
            mutation($input: PerformedProcedureCreateInput!) {
                performedProcedure {
                    create(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public createPerformedProcedures(input: PerformedProcedureCreateInput[]) {
        return this.mutate(
            `
            mutation($input: [PerformedProcedureCreateInput!]!) {
                performedProcedure {
                    createMany(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public updatePerformedProcedure(input: UpdatePerformedProcedureInput) {
        return this.mutate(
            `
            mutation($input: UpdatePerformedProcedureInput!) {
                performedProcedure {
                    update(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    public deletePerformedProcedure(input: DeletePerformedProcedureInput) {
        return this.mutate(
            `
            mutation($input: DeletePerformedProcedureInput!) {
                performedProcedure {
                    delete(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    exportWorks(input: WorkExportInput): Promise<FolderPrintObject> {
        return this.query(
            `
                query($input: WorkExportInput!) {
                    work {
                        export(input: $input) {
                            fileName
                            pdfBase64
                        }
                    }
                }
            `,
            { input },
        );
    }

    public prepareWorkExport(
        input: PrepareWorkExportInput,
    ): Promise<WorkExportPreparationObject> {
        return this.query<WorkExportPreparationObject>(
            `
                query($input: PrepareWorkExportInput!) {
                    work {
                        prepareExport(input: $input) {
                            services {
                                serviceId
                                serviceName
                                color
                                headers {
                                    id
                                    name
                                }
                                footers {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    tangibleDepositExport(input: TangibleDepositExportInput): Promise<FolderPrintObject> {
        return this.query(
            `
                query($input: TangibleDepositExportInput!) {
                    work {
                        tangibleDepositExport(input: $input) {
                            fileName
                            pdfBase64
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceLocations(input: GridInput) {
        return this.query<ServiceLocationGridObject>(
            `
                query($input: GridInput) {
                    serviceLocation {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${serviceLocationItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceBasicInfos(input: GridInput | null, frag: string = serviceBasicInfoGridItemFrag): Promise<ServiceBasicInfoGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    serviceBasicInfo {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceBasicInfo(input: ServiceBasicInfoCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceBasicInfoCreateInput!) {
                    serviceBasicInfo {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceBasicInfo(input: ServiceBasicInfoUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceBasicInfoUpdateInput!) {
                    serviceBasicInfo {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceBasicInfo(input: ServiceBasicInfoDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceBasicInfoDeleteInput!) {
                    serviceBasicInfo {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceMedicalProcedures(
        input: GridInput,
        frag: string = serviceMedicalProcedureGridItemFrag,
        options: GqlQueryOptions = {},
    ) {
        return this.query<ServiceMedicalProcedureGridObject>(
            `
                query($input: GridInput) {
                    serviceMedicalProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
            options,
        );
    }

    public getMedicalProcedureHistory(input: GridInput, fragment: string) {
        return this.query<MedicalProcedureGridHistoryObject>(
            `
                query($input: GridInput) {
                    medicalProcedure {
                        filterHistory(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getVoucherPrescribedMedicalProcedures(input: GridInput, frag: string = voucherPrescribedMedicalProcedureFrag) {
        return this.query<VoucherPrescribedMedicalProcedureGridObject>(
            `
                query($input: GridInput) {
                    voucherPrescribedMedicalProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceMedicalProcedure(input: ServiceMedicalProcedureCreateInput) {
        return this.mutate(
            `
                mutation($input: ServiceMedicalProcedureCreateInput!) {
                    serviceMedicalProcedure {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceMedicalProcedure(input: ServiceMedicalProcedureUpdateInput) {
        return this.mutate(
            `
                mutation($input: ServiceMedicalProcedureUpdateInput!) {
                    serviceMedicalProcedure {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceMedicalProcedure(input: IdInput) {
        return this.mutate(
            `
                mutation($input: IdInput!) {
                    serviceMedicalProcedure {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServicePayment(input: ServicePaymentGetInput, frag: string = servicePaymentFrag): Promise<ServicePaymentObject> {
        return this.query(
            `
                query($input: ServicePaymentGetInput!) {
                    servicePayment {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServicePayments(input: GridInput | null, frag: string = servicePaymentGridItemFrag): Promise<ServicePaymentGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    servicePayment {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServicePayment(input: ServicePaymentCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentCreateInput!) {
                    servicePayment {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServicePayment(input: ServicePaymentUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentUpdateInput!) {
                    servicePayment {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServicePayment(input: ServicePaymentDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentDeleteInput!) {
                    servicePayment {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServicePaymentMedicalProcedures(input: GridInput | null, frag: string = servicePaymentMedicalProcedureGridItemFrag): Promise<ServicePaymentMedicalProcedureGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    servicePaymentMedicalProcedure {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServicePaymentMedicalProcedure(input: ServicePaymentMedicalProcedureCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentMedicalProcedureCreateInput!) {
                    servicePaymentMedicalProcedure {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServicePaymentMedicalProcedure(input: ServicePaymentMedicalProcedureUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentMedicalProcedureUpdateInput!) {
                    servicePaymentMedicalProcedure {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServicePaymentMedicalProcedure(input: ServicePaymentMedicalProcedureDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePaymentMedicalProcedureDeleteInput!) {
                    servicePaymentMedicalProcedure {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServicePatternStructure(input: ServicePatternStructureCreateInput, frag: string = servicePatternObjectFrag) {
        return this.mutate<ServicePatternObject>(
            `
                mutation($input: ServicePatternStructureCreateInput!) {
                    servicePattern {
                        createStructure(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServicePatternDocument(input: ServicePatternDocumentCreateInput, frag: string = servicePatternObjectFrag) {
        return this.mutate<ServicePatternObject>(
            `
                mutation($input: ServicePatternDocumentCreateInput!) {
                    servicePattern {
                        createDocument(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServicePatternStructure(input: ServicePatternStructureUpdateInput, frag: string = servicePatternObjectFrag) {
        return this.mutate<ServicePatternObject>(
            `
                mutation($input: ServicePatternStructureUpdateInput!) {
                    servicePattern {
                        updateStructure(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServicePatternDocument(input: ServicePatternDocumentUpdateInput, frag: string = servicePatternObjectFrag) {
        return this.mutate<ServicePatternObject>(
            `
                mutation($input: ServicePatternDocumentUpdateInput!) {
                    servicePattern {
                        updateDocument(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServicePatterns(input: GridInput, frag: string = servicePatternObjectFrag) {
        return this.query<ServicePatternGridObject>(
            `
                query($input: GridInput) {
                    servicePattern {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServicePattern(input: ServicePatternDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServicePatternDeleteInput!) {
                    servicePattern {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getProcessedPattern(input: ProcessPatternInput) {
        return this.query<ProcessPatternContentObject>(
            `
                query($input: ProcessPatternInput!) {
                    servicePattern {
                        processPattern(input: $input) {
                            content
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDoctorSpecializations(input: GridInput | null = null, frag: string = doctorSpecializationFrag) {
        return this.query<DoctorSpecializationGridObject>(
            `
                query($input: GridInput) {
                    doctorSpecialization {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createDoctorSpecialization(input: DoctorSpecializationCreateInput, fragment: string = doctorSpecializationFrag) {
        return this.mutate<DoctorSpecializationObject>(
            `
                mutation($input: DoctorSpecializationCreateInput!) {
                    doctorSpecialization {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateDoctorSpecialization(input: DoctorSpecializationUpdateInput, fragment: string = doctorSpecializationFrag) {
        return this.mutate<DoctorSpecializationObject>(
            `
                mutation($input: DoctorSpecializationUpdateInput!) {
                    doctorSpecialization {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteDoctorSpecialization(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    doctorSpecialization {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public importDoctorSpecializations(input: DoctorSpecializationImportInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DoctorSpecializationImportInput!) {
                    doctorSpecialization {
                        import(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceNursingCareDiagnosisCodes(input: GridInput | null, frag: string = serviceNursingCareDiagnosisCodeGridItemFrag): Promise<ServiceNursingCareDiagnosisCodeGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    serviceNursingCareDiagnosisCode {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceNursingCareDiagnosisCode(input: ServiceNursingCareDiagnosisCodeCreateInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: ServiceNursingCareDiagnosisCodeCreateInput!) {
                serviceNursingCareDiagnosisCode {
                    create(input: $input) {
                        ok
                    }
                }
            }
            `,
            { input },
        );
    }

    public updateServiceNursingCareDiagnosisCode(input: ServiceNursingCareDiagnosisCodeUpdateInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: ServiceNursingCareDiagnosisCodeUpdateInput!) {
                serviceNursingCareDiagnosisCode {
                    update(input: $input) {
                        ok
                    }
                }
            }
            `,
            { input },
        );
    }

    public deleteServiceNursingCareDiagnosisCode(input: IdInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: IdInput!) {
                serviceNursingCareDiagnosisCode {
                    delete(input: $input) {
                        ok
                    }
                }
            }
            `,
            { input },
        );
    }

    public getServiceNursingCareDiagnosisGroups(input: GridInput | null, frag: string = serviceNursingCareDiagnosisGroupGridItemFrag): Promise<ServiceNursingCareDiagnosisGroupGridObject> {
        return this.query(
            `
                query($input: GridInput) {
                    serviceNursingCareDiagnosisGroup {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceNursingCareDiagnosisGroup(input: ServiceNursingCareDiagnosisGroupCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceNursingCareDiagnosisGroupCreateInput!) {
                    serviceNursingCareDiagnosisGroup {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceNursingCareDiagnosisGroup(input: ServiceNursingCareDiagnosisGroupUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceNursingCareDiagnosisGroupUpdateInput!) {
                    serviceNursingCareDiagnosisGroup {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceNursingCareDiagnosisGroup(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceNursingCareDiagnosisGroup {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceDataShares(input: GridInput, frag: string = serviceDataShareFrag) {
        return this.query<ServiceDataShareGridObject>(
            `
                query($input: GridInput!) {
                    serviceDataShare {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSharingServices(input: GridInput, frag: string = serviceDataShareFrag) {
        return this.query<ServiceDataShareGridObject>(
            `
                query($input: GridInput!) {
                    serviceDataShare {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceDataShare(input: ServiceDataShareCreateInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceDataShareCreateInput!) {
                    serviceDataShare {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateServiceDataShare(input: ServiceDataShareUpdateInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: ServiceDataShareUpdateInput!) {
                    serviceDataShare {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceDataShare(input: IdInput, frag: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceDataShare {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceLogs(input: GridInput, frag: string = historyGridItemFrag) {
        return this.query<AuditLogGridObject>(
            `
                query($input: GridInput) {
                    auditLog {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getServicesPublicFilter(input: GridInput, fragment: string): Promise<ServiceGridObject> {
        return this.query<ServiceGridObject>(
            `
                query($input: GridInput!) {
                    service {
                        publicFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

}
